// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { Moves, useInsertBatchJobMutation } from '@gql/schema';
import { Modal, ModalHeader, ModalContent, ModalFooter, ModalAction } from '@components/Modal';
import { TextField, Autocomplete, Box, Link, Typography } from '@mui/material';
import { config } from '@config';

// TYPES ---------------------------------------------------------------- //

interface MockSequenceModalProps {
  open: boolean;
  onClose: () => void;
  move?: Moves;
}

interface MockSequenceFormData {
  sequence: string;
  interval: number | '';
  accessorialCode: string;
  amount: number | '';
}

// CONSTANTS ---------------------------------------------------------------- //

const SEQUENCE_OPTIONS = [
  // Most Common
  'new-to-successful',
  'reset',
  'add-charge',
  // Status Transitions
  'new-to-delivery-started',
  'new-to-successful-many-charges',
  'pending-to-partial',
  'pending-to-failed',
  'pending-to-success',
  // Charge Types
  'class-three-charge',
  'delay-charge',
  'fuel-stop-charge',
  'partial-cancel-charge',
  'roadside-tow-charge',
  'surge-ride-charge',
  // Operations
  'return-ready',
  'settle-move',
];

// COMPONENT ---------------------------------------------------------------- //

const MockSequenceModal = ({ open, onClose, move }: MockSequenceModalProps) => {
  console.log('move', move);
  const [formData, setFormData] = React.useState<MockSequenceFormData>({
    sequence: '',
    interval: 2000,
    accessorialCode: '',
    amount: '',
  });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const [insertBatchJob] = useInsertBatchJobMutation();


  const handleClose = () => {
    // Reset form when closing
    setFormData({
      sequence: '',
      interval: 2000,
      accessorialCode: '',
      amount: '',
    });
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (!move?.id) {
      toast.error('Move ID is required');
      return;
    }

    setIsSubmitting(true);

    try {
      const batchId = uuidv4();

      // Build the input object for the batch job
      const inputData: any = {
        moveId: move.id,
        options: {
          sequence: formData.sequence,
        },
      };

      // Add optional fields only if they have values
      if (formData.interval !== '') {
        inputData.options.interval = formData.interval;
      }

      if (formData.sequence === 'add-charge') {
        if (formData.accessorialCode !== '') {
          inputData.options.accessorialCode = formData.accessorialCode;
        }
        if (formData.amount !== '') {
          inputData.options.amount = formData.amount;
        }
      }

      const response = await insertBatchJob({
        variables: {
          batch_id: batchId,
          trigger_type: 'mock',
          input: inputData,
        },
      });

      if (response.data?.insert_batch_jobs_one?.id) {
        toast.success(`Mock sequence created successfully!`);
        handleClose();
      } else {
        toast.error('Failed to create mock sequence');
      }
    } catch (error) {
      console.error('Error creating mock sequence:', error);
      toast.error('Failed to create mock sequence. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFieldChange = (field: keyof MockSequenceFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const isFormValid =
    formData.sequence !== '' &&
    (formData.sequence !== 'add-charge' || (formData.accessorialCode !== '' && formData.amount !== '')) &&
    !isSubmitting;

  return (
    <Modal open={open}>
      <ModalHeader handleClose={handleClose}>Mock Sequence for Move #{move && move.id}</ModalHeader>

      <ModalContent
        subtitle={`Simulate a move progressing through different stages and statuses. This will trigger the mock endpoint for testing purposes.`}
      >
        <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
          <Link 
            href="https://api.hopdrive.com/docs/mock-post" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            See our documentation about mock sequences
          </Link>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Autocomplete
            fullWidth
            disableClearable
            options={SEQUENCE_OPTIONS}
            value={formData.sequence || null}
            onChange={(_event, value) => handleFieldChange('sequence', value || '')}
            renderInput={params => (
              <TextField
                {...params}
                label='Sequence'
                placeholder='Select or type sequence...'
                required
                size='small'
                fullWidth
              />
            )}
          />

          <TextField
            label='Interval (in milliseconds)'
            type='number'
            size='small'
            fullWidth
            value={formData.interval}
            onChange={e => handleFieldChange('interval', e.target.value ? Number(e.target.value) : '')}
            placeholder='Enter interval (optional)'
          />

          {formData.sequence === 'add-charge' && (
            <>
              <TextField
                label='Accessorial Code'
                size='small'
                fullWidth
                required
                value={formData.accessorialCode}
                onChange={e => handleFieldChange('accessorialCode', e.target.value)}
                placeholder='Enter accessorial code (required)'
              />

              <TextField
                label='Amount'
                type='number'
                size='small'
                fullWidth
                required
                value={formData.amount}
                onChange={e => handleFieldChange('amount', e.target.value ? Number(e.target.value) : '')}
                placeholder='Enter amount (required)'
              />
            </>
          )}

        </Box>
      </ModalContent>

      <ModalFooter>
        <ModalAction onClick={() => handleClose()} color='secondary'>
          Cancel
        </ModalAction>
        <ModalAction onClick={() => handleSubmit()} disabled={!isFormValid}>
          Execute Mock Sequence
        </ModalAction>
      </ModalFooter>
    </Modal>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default MockSequenceModal;
